import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, ArrowLeft, Sparkles, Eye, Send, FileText, Globe, Loader2, Lock, Image, Calendar, BarChart3, Clock, TrendingUp, MousePointer } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [stockImages, setStockImages] = useState([]);
  const [previewPost, setPreviewPost] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [mainView, setMainView] = useState('posts'); // 'posts' | 'analytics'
  const [analytics, setAnalytics] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', image_url: '', category: '', read_time: '',
  });

  useEffect(() => {
    const saved = sessionStorage.getItem('dhanamm_admin');
    if (saved === 'true') {
      setIsAuthenticated(true);
      fetchPosts();
      fetchImages();
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      sessionStorage.setItem('dhanamm_admin', 'true');
      fetchPosts();
      fetchImages();
    } catch {
      setLoginError('Invalid password. Please try again.');
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/stock-images`);
      setStockImages(res.data);
    } catch {}
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', excerpt: '', content: '', image_url: '', category: '', read_time: '' });
    setEditingPost(null);
    setIsCreating(false);
    setShowAiPanel(false);
    setShowBulkPanel(false);
    setPreviewPost(null);
    setShowImageLibrary(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/blog`, { ...formData, status: 'draft' });
      toast.success('Draft saved!');
      resetForm();
      fetchPosts();
    } catch { toast.error('Failed to save draft.'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/blog/${editingPost.id}`, formData);
      toast.success('Post updated!');
      resetForm();
      fetchPosts();
    } catch { toast.error('Failed to update.'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`);
      toast.success('Post deleted.');
      fetchPosts();
    } catch { toast.error('Failed to delete.'); }
  };

  const handlePublish = async (postId) => {
    try {
      await axios.put(`${API}/admin/blog/${postId}/publish`);
      toast.success('Published!');
      fetchPosts();
    } catch { toast.error('Failed to publish.'); }
  };

  const handleUnpublish = async (postId) => {
    try {
      await axios.put(`${API}/admin/blog/${postId}/unpublish`);
      toast.success('Moved to drafts.');
      fetchPosts();
    } catch { toast.error('Failed.'); }
  };

  const handleSchedule = async (postId) => {
    if (!scheduleDate) { toast.error('Select a date/time.'); return; }
    try {
      const utcDate = new Date(scheduleDate).toISOString();
      await axios.put(`${API}/admin/blog/${postId}/schedule`, { scheduled_date: utcDate });
      toast.success('Post scheduled!');
      setShowScheduleModal(null);
      setScheduleDate('');
      fetchPosts();
    } catch { toast.error('Failed to schedule.'); }
  };

  const handleUnschedule = async (postId) => {
    try {
      await axios.put(`${API}/admin/blog/${postId}/unschedule`);
      toast.success('Schedule removed.');
      fetchPosts();
    } catch { toast.error('Failed.'); }
  };

  const handleAiGenerate = async () => {
    if (!aiTopic.trim()) { toast.error('Enter a topic.'); return; }
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/blog/ai-generate`, { topic: aiTopic });
      setFormData({
        title: res.data.title || '', excerpt: res.data.excerpt || '', content: res.data.content || '',
        image_url: '', category: res.data.category || 'Market Analysis', read_time: res.data.read_time || '8 min read',
      });
      setShowAiPanel(false);
      setIsCreating(true);
      toast.success('AI draft generated!');
    } catch { toast.error('Generation failed.'); }
    finally { setIsGenerating(false); }
  };

  const handleBulkGenerate = async () => {
    setIsBulkGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/blog/bulk-generate`, {
        topics: [
          'Latest Indian stock market trends and Nifty 50 outlook this week',
          'Smart investment strategies for retail investors in India',
          'Weekend market recap and key trading opportunities ahead',
        ]
      }, { timeout: 120000 });
      toast.success(`Generated ${res.data.generated} drafts!`);
      setShowBulkPanel(false);
      fetchPosts();
    } catch { toast.error('Bulk generation failed.'); }
    finally { setIsBulkGenerating(false); }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setIsCreating(false);
    setPreviewPost(null);
    setFormData({ title: post.title, excerpt: post.excerpt, content: post.content, image_url: post.image_url, category: post.category, read_time: post.read_time });
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const showForm = isCreating || editingPost;
  const filteredPosts = activeTab === 'all' ? posts : posts.filter((p) => (p.status || 'published') === activeTab);
  const draftCount = posts.filter((p) => (p.status || 'published') === 'draft').length;
  const publishedCount = posts.filter((p) => (p.status || 'published') === 'published').length;
  const scheduledCount = posts.filter((p) => (p.status || 'published') === 'scheduled').length;

  const getNextScheduleTimes = () => {
    const now = new Date();
    const IST_OFFSET = 5.5 * 60 * 60 * 1000;
    const slots = [];
    for (let d = 0; d < 14; d++) {
      const date = new Date(now.getTime() + d * 86400000);
      const day = date.getDay();
      if (day === 1 || day === 3 || day === 6) {
        const istDate = new Date(date);
        istDate.setHours(2, 30, 0, 0);
        if (istDate > now) {
          slots.push(istDate);
        }
      }
    }
    return slots.slice(0, 6);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="w-full max-w-md p-8 bg-white border border-[rgba(10,15,26,0.05)]" data-testid="admin-login">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FFF4E6' }}>
              <Lock size={28} style={{ color: '#FF5722' }} />
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>Admin Access</h1>
            <p className="text-sm mt-2" style={{ color: '#475569' }}>Enter password to manage blog posts</p>
          </div>
          <form onSubmit={handleLogin}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input"
              placeholder="Enter admin password" className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] mb-4"
              style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} autoFocus />
            {loginError && <p className="text-sm mb-4" style={{ color: '#EF4444' }} data-testid="login-error">{loginError}</p>}
            <button type="submit" data-testid="admin-login-button" className="w-full py-3 font-semibold"
              style={{ backgroundColor: '#FF5722', color: 'white', border: 'none', cursor: 'pointer' }}>Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="py-12 md:py-16" style={{ backgroundColor: '#FAFAFA', minHeight: '100vh' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium mb-3 hover:text-[#FF5722] transition-colors" style={{ color: '#475569' }}>
                <ArrowLeft size={16} /> Back to Blog
              </Link>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }} data-testid="admin-heading">Blog Dashboard</h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setMainView('posts'); resetForm(); }} data-testid="view-posts-btn"
                className="inline-flex items-center gap-2 text-xs sm:text-sm px-3 py-2"
                style={{ backgroundColor: mainView === 'posts' ? '#0A0F1A' : 'white', color: mainView === 'posts' ? 'white' : '#475569', border: mainView === 'posts' ? 'none' : '1px solid #E2E8F0', fontWeight: 600, cursor: 'pointer' }}>
                <FileText size={14} /> Posts
              </button>
              <button onClick={() => { setMainView('analytics'); fetchAnalytics(); }} data-testid="view-analytics-btn"
                className="inline-flex items-center gap-2 text-xs sm:text-sm px-3 py-2"
                style={{ backgroundColor: mainView === 'analytics' ? '#0A0F1A' : 'white', color: mainView === 'analytics' ? 'white' : '#475569', border: mainView === 'analytics' ? 'none' : '1px solid #E2E8F0', fontWeight: 600, cursor: 'pointer' }}>
                <BarChart3 size={14} /> Analytics
              </button>
            </div>
          </div>

          {/* Analytics View */}
          {mainView === 'analytics' && (
            <div data-testid="analytics-view">
              {!analytics ? (
                <p className="text-center py-12" style={{ color: '#475569' }}>Loading analytics...</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                      { label: 'Total Views', value: analytics.total_views, icon: <Eye size={20} />, color: '#0052FF' },
                      { label: 'Total Clicks', value: analytics.total_clicks, icon: <MousePointer size={20} />, color: '#FF5722' },
                      { label: 'Views (7 days)', value: analytics.recent_views_7d, icon: <TrendingUp size={20} />, color: '#10B981' },
                      { label: 'Clicks (7 days)', value: analytics.recent_clicks_7d, icon: <MousePointer size={20} />, color: '#F59E0B' },
                    ].map((stat, i) => (
                      <div key={i} className="bg-white border border-[rgba(10,15,26,0.05)] p-6" data-testid={`analytics-stat-${i}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                            {stat.icon}
                          </div>
                        </div>
                        <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>{stat.value}</p>
                        <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white border border-[rgba(10,15,26,0.05)]">
                    <div className="p-4 border-b border-[#E2E8F0]">
                      <h3 className="font-semibold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>Post Performance</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr style={{ backgroundColor: '#F8FAFC' }}>
                            <th className="text-left text-xs font-semibold px-4 py-3" style={{ color: '#475569' }}>Article</th>
                            <th className="text-right text-xs font-semibold px-4 py-3" style={{ color: '#475569' }}>Views</th>
                            <th className="text-right text-xs font-semibold px-4 py-3" style={{ color: '#475569' }}>Clicks</th>
                            <th className="text-right text-xs font-semibold px-4 py-3" style={{ color: '#475569' }}>CTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.posts.map((post, i) => (
                            <tr key={post.id} className="border-t border-[#E2E8F0]" data-testid={`analytics-row-${i}`}>
                              <td className="px-4 py-3 text-sm" style={{ color: '#0A0F1A' }}>{post.title.substring(0, 60)}{post.title.length > 60 ? '...' : ''}</td>
                              <td className="px-4 py-3 text-sm text-right font-semibold" style={{ color: '#0A0F1A' }}>{post.views}</td>
                              <td className="px-4 py-3 text-sm text-right font-semibold" style={{ color: '#FF5722' }}>{post.clicks}</td>
                              <td className="px-4 py-3 text-sm text-right">
                                <span className="px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: post.ctr > 5 ? '#ECFDF5' : '#F8FAFC', color: post.ctr > 5 ? '#10B981' : '#94A3B8' }}>
                                  {post.ctr}%
                                </span>
                              </td>
                            </tr>
                          ))}
                          {analytics.posts.length === 0 && (
                            <tr><td colSpan="4" className="text-center py-8 text-sm" style={{ color: '#94A3B8' }}>No data yet. Views are tracked when visitors read your blog posts.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Posts View */}
          {mainView === 'posts' && (
            <>
              {/* Action buttons */}
              {!showForm && !showAiPanel && !showBulkPanel && (
                <div className="flex gap-2 flex-wrap mb-6">
                  <button onClick={() => { resetForm(); setShowBulkPanel(true); }} data-testid="bulk-generate-button"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm"
                    style={{ backgroundColor: '#0A0F1A', color: 'white', fontWeight: 600, padding: '0.6rem 1rem', border: 'none', cursor: 'pointer' }}>
                    <Calendar size={14} /> Weekly Posts
                  </button>
                  <button onClick={() => { resetForm(); setShowAiPanel(true); }} data-testid="ai-generate-button"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm"
                    style={{ backgroundColor: '#475569', color: 'white', fontWeight: 600, padding: '0.6rem 1rem', border: 'none', cursor: 'pointer' }}>
                    <Sparkles size={14} /> AI Draft
                  </button>
                  <button onClick={() => { resetForm(); setIsCreating(true); }} data-testid="create-post-button"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm"
                    style={{ backgroundColor: '#FF5722', color: 'white', fontWeight: 600, padding: '0.6rem 1rem', border: 'none', cursor: 'pointer' }}>
                    <Plus size={14} /> New Post
                  </button>
                </div>
              )}

              {/* Bulk panel */}
              {showBulkPanel && (
                <div className="bg-white border border-[rgba(10,15,26,0.05)] p-8 mb-8" data-testid="bulk-panel">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3"><Calendar size={22} style={{ color: '#FF5722' }} />
                      <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>Generate Weekly Posts</h2></div>
                    <button onClick={resetForm} className="hover:text-[#FF5722]"><X size={20} /></button>
                  </div>
                  <p className="text-sm mb-4" style={{ color: '#475569' }}>Generate 3 drafts for Mon/Wed/Sat. AI will research and create ready-to-review drafts.</p>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {['Monday 8 AM IST', 'Wednesday 8 AM IST', 'Saturday 8 AM IST'].map((day, i) => (
                      <div key={i} className="p-4 text-center" style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                        <p className="font-semibold text-sm" style={{ color: '#0A0F1A' }}>{day}</p>
                        <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{['Market Trends', 'Strategies', 'Recap & Tips'][i]}</p>
                      </div>
                    ))}
                  </div>
                  <button onClick={handleBulkGenerate} disabled={isBulkGenerating} data-testid="bulk-generate-submit"
                    className="inline-flex items-center gap-2"
                    style={{ backgroundColor: isBulkGenerating ? '#94A3B8' : '#FF5722', color: 'white', fontWeight: 600, padding: '0.75rem 1.5rem', border: 'none', cursor: isBulkGenerating ? 'not-allowed' : 'pointer' }}>
                    {isBulkGenerating ? (<><Loader2 size={16} className="animate-spin" /> Generating (~1 min)...</>) : (<><Sparkles size={16} /> Generate 3 Weekly Drafts</>)}
                  </button>
                </div>
              )}

              {/* AI panel */}
              {showAiPanel && (
                <div className="bg-white border border-[rgba(10,15,26,0.05)] p-8 mb-8" data-testid="ai-panel">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3"><Sparkles size={22} style={{ color: '#FF5722' }} />
                      <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>AI Blog Writer</h2></div>
                    <button onClick={resetForm} className="hover:text-[#FF5722]"><X size={20} /></button>
                  </div>
                  <div className="flex gap-3">
                    <input type="text" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} data-testid="ai-topic-input"
                      placeholder="e.g. Best SIP strategies for 2026..." className="flex-1 px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722]"
                      style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} onKeyDown={(e) => e.key === 'Enter' && handleAiGenerate()} disabled={isGenerating} />
                    <button onClick={handleAiGenerate} disabled={isGenerating} data-testid="ai-generate-submit"
                      className="inline-flex items-center gap-2 whitespace-nowrap"
                      style={{ backgroundColor: isGenerating ? '#94A3B8' : '#FF5722', color: 'white', fontWeight: 600, padding: '0.75rem 1.5rem', border: 'none', cursor: isGenerating ? 'not-allowed' : 'pointer' }}>
                      {isGenerating ? (<><Loader2 size={16} className="animate-spin" /> Researching...</>) : (<><Sparkles size={16} /> Generate</>)}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {['Nifty 50 outlook', 'Best mutual funds', 'How to read balance sheet', 'IPO strategies'].map((s) => (
                      <button key={s} onClick={() => setAiTopic(s)} className="text-xs px-3 py-1.5 border border-[#E2E8F0] hover:border-[#FF5722] hover:text-[#FF5722]" style={{ color: '#475569', backgroundColor: 'white' }}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Create/Edit form */}
              {showForm && (
                <div className="bg-white border border-[rgba(10,15,26,0.05)] p-8 mb-8" data-testid="blog-form">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>{editingPost ? 'Edit Post' : 'Create New Draft'}</h2>
                    <button onClick={resetForm} className="hover:text-[#FF5722]" data-testid="form-cancel-button"><X size={20} /></button>
                  </div>
                  <form onSubmit={editingPost ? handleUpdate : handleCreate}>
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#0A0F1A' }}>Title *</label>
                        <input type="text" name="title" data-testid="form-title-input" value={formData.title} onChange={handleChange} required
                          className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722]" style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} />
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: '#0A0F1A' }}>Category *</label>
                          <input type="text" name="category" data-testid="form-category-input" value={formData.category} onChange={handleChange} required placeholder="e.g. Trading Tips"
                            className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722]" style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: '#0A0F1A' }}>Read Time *</label>
                          <input type="text" name="read_time" data-testid="form-readtime-input" value={formData.read_time} onChange={handleChange} required placeholder="e.g. 8 min read"
                            className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722]" style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: '#0A0F1A' }}>Image</label>
                          <button type="button" onClick={() => setShowImageLibrary(!showImageLibrary)} data-testid="open-image-library"
                            className="w-full px-4 py-3 border border-[#E2E8F0] hover:border-[#FF5722] flex items-center gap-2 text-left"
                            style={{ color: formData.image_url ? '#0A0F1A' : '#94A3B8', backgroundColor: '#FAFAFA' }}>
                            <Image size={16} /> {formData.image_url ? 'Change Image' : 'Pick from Library'}
                          </button>
                        </div>
                      </div>
                      {showImageLibrary && (
                        <div className="p-4" style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium" style={{ color: '#0A0F1A' }}>Image Library</p>
                            <button type="button" onClick={() => setShowImageLibrary(false)} className="text-xs" style={{ color: '#94A3B8' }}>Close</button>
                          </div>
                          <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                            {stockImages.map((img) => (
                              <button key={img.id} type="button" onClick={() => { setFormData({ ...formData, image_url: img.url }); setShowImageLibrary(false); }}
                                className="relative group overflow-hidden aspect-video" style={{ border: formData.image_url === img.url ? '3px solid #FF5722' : '2px solid transparent' }}>
                                <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-end">
                                  <p className="text-white text-xs p-1 opacity-0 group-hover:opacity-100">{img.label}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                          <input type="url" name="image_url" value={formData.image_url} onChange={handleChange} placeholder="Or paste custom URL..."
                            className="w-full mt-3 px-3 py-2 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] text-sm" style={{ color: '#0A0F1A', backgroundColor: 'white' }} />
                        </div>
                      )}
                      {formData.image_url && (
                        <div className="flex items-center gap-3">
                          <img src={formData.image_url} alt="Selected" className="w-24 h-16 object-cover" />
                          <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="text-xs hover:text-[#EF4444]" style={{ color: '#94A3B8' }}>Remove</button>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#0A0F1A' }}>Excerpt *</label>
                        <textarea name="excerpt" data-testid="form-excerpt-input" value={formData.excerpt} onChange={handleChange} required rows="2" placeholder="Short summary..."
                          className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] resize-none" style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#0A0F1A' }}>Content * (Markdown)</label>
                        <textarea name="content" data-testid="form-content-input" value={formData.content} onChange={handleChange} required rows="16" placeholder="Write article..."
                          className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] resize-vertical font-mono text-sm" style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }} />
                      </div>
                      <div className="flex gap-3 flex-wrap">
                        <button type="submit" data-testid="form-submit-button" className="inline-flex items-center gap-2"
                          style={{ backgroundColor: '#FF5722', color: 'white', fontWeight: 600, padding: '0.75rem 1.5rem', border: 'none', cursor: 'pointer' }}>
                          <Save size={16} /> {editingPost ? 'Update' : 'Save Draft'}
                        </button>
                        <button type="button" onClick={() => setPreviewPost({ ...formData })} className="inline-flex items-center gap-2"
                          style={{ backgroundColor: 'transparent', border: '2px solid #0A0F1A', color: '#0A0F1A', fontWeight: 600, padding: '0.75rem 1.5rem', cursor: 'pointer' }}>
                          <Eye size={16} /> Preview
                        </button>
                        <button type="button" onClick={resetForm} style={{ border: '1px solid #E2E8F0', color: '#475569', fontWeight: 500, padding: '0.75rem 1.5rem', cursor: 'pointer', backgroundColor: 'transparent' }}>Cancel</button>
                      </div>
                    </div>
                  </form>
                </div>
              )}

              {previewPost && (
                <div className="bg-white border border-[rgba(10,15,26,0.05)] p-8 mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>Preview</h2>
                    <button onClick={() => setPreviewPost(null)} className="hover:text-[#FF5722]"><X size={20} /></button>
                  </div>
                  <div className="max-w-3xl">
                    {previewPost.image_url && <img src={previewPost.image_url} alt="" className="w-full h-48 object-cover mb-6" />}
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1 mb-4 inline-block" style={{ backgroundColor: '#FFF4E6', color: '#FF5722' }}>{previewPost.category || 'Uncategorized'}</span>
                    <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>{previewPost.title || 'Untitled'}</h3>
                    <p className="text-base mb-6" style={{ color: '#475569' }}>{previewPost.excerpt}</p>
                    <div className="text-sm whitespace-pre-wrap" style={{ color: '#475569' }}>{previewPost.content?.substring(0, 1000)}{previewPost.content?.length > 1000 && '...'}</div>
                  </div>
                </div>
              )}

              {/* Post list with tabs */}
              {!showForm && !showAiPanel && !showBulkPanel && (
                <>
                  <div className="flex gap-2 mb-6 flex-wrap">
                    {[
                      { key: 'all', label: `All (${posts.length})`, icon: <FileText size={14} /> },
                      { key: 'draft', label: `Drafts (${draftCount})`, icon: <Pencil size={14} /> },
                      { key: 'scheduled', label: `Scheduled (${scheduledCount})`, icon: <Clock size={14} /> },
                      { key: 'published', label: `Published (${publishedCount})`, icon: <Globe size={14} /> },
                    ].map((tab) => (
                      <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
                        className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2"
                        style={{ backgroundColor: activeTab === tab.key ? '#0A0F1A' : 'white', color: activeTab === tab.key ? 'white' : '#475569', border: activeTab === tab.key ? 'none' : '1px solid #E2E8F0' }}>
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  {isLoading ? <p className="text-center py-8" style={{ color: '#475569' }}>Loading...</p> : filteredPosts.length === 0 ? (
                    <div className="text-center py-16 bg-white border border-[rgba(10,15,26,0.05)]">
                      <FileText size={48} style={{ color: '#E2E8F0' }} className="mx-auto mb-4" />
                      <p style={{ color: '#475569' }}>No posts in this category</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredPosts.map((post, idx) => {
                        const status = post.status || 'published';
                        return (
                          <div key={post.id} data-testid={`admin-post-${idx}`} className="bg-white border border-[rgba(10,15,26,0.05)] p-5 flex items-center gap-5">
                            {post.image_url ? <img src={post.image_url} alt="" className="w-20 h-14 object-cover flex-shrink-0" /> : (
                              <div className="w-20 h-14 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#F1F5F9' }}><FileText size={20} style={{ color: '#94A3B8' }} /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-semibold truncate" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>{post.title}</h3>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <span className="text-xs font-semibold px-2 py-0.5"
                                  style={{ backgroundColor: status === 'published' ? '#ECFDF5' : status === 'scheduled' ? '#EFF6FF' : '#FFF4E6', color: status === 'published' ? '#10B981' : status === 'scheduled' ? '#3B82F6' : '#F59E0B' }}>
                                  {status === 'published' ? 'Published' : status === 'scheduled' ? 'Scheduled' : 'Draft'}
                                </span>
                                {status === 'scheduled' && post.scheduled_date && (
                                  <span className="text-xs flex items-center gap-1" style={{ color: '#3B82F6' }}>
                                    <Clock size={12} /> {new Date(post.scheduled_date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })}
                                  </span>
                                )}
                                <span className="text-xs uppercase tracking-wider font-medium px-2 py-0.5" style={{ backgroundColor: '#F1F5F9', color: '#475569' }}>{post.category}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                              {status === 'draft' && (
                                <>
                                  <button onClick={() => { setShowScheduleModal(post.id); setScheduleDate(''); }} data-testid={`schedule-post-${idx}`}
                                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5"
                                    style={{ backgroundColor: '#3B82F6', color: 'white', border: 'none', cursor: 'pointer' }}>
                                    <Clock size={12} /> Schedule
                                  </button>
                                  <button onClick={() => handlePublish(post.id)} data-testid={`publish-post-${idx}`}
                                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5"
                                    style={{ backgroundColor: '#10B981', color: 'white', border: 'none', cursor: 'pointer' }}>
                                    <Send size={12} /> Publish
                                  </button>
                                </>
                              )}
                              {status === 'scheduled' && (
                                <button onClick={() => handleUnschedule(post.id)} data-testid={`unschedule-post-${idx}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5"
                                  style={{ backgroundColor: '#F59E0B', color: 'white', border: 'none', cursor: 'pointer' }}>
                                  <X size={12} /> Unschedule
                                </button>
                              )}
                              {status === 'published' && (
                                <button onClick={() => handleUnpublish(post.id)} data-testid={`unpublish-post-${idx}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5"
                                  style={{ backgroundColor: '#F59E0B', color: 'white', border: 'none', cursor: 'pointer' }}>
                                  <FileText size={12} /> Unpublish
                                </button>
                              )}
                              <button onClick={() => startEdit(post)} data-testid={`edit-post-${idx}`} className="p-2 hover:bg-[#F1F5F9]"><Pencil size={16} style={{ color: '#0052FF' }} /></button>
                              <button onClick={() => handleDelete(post.id)} data-testid={`delete-post-${idx}`} className="p-2 hover:bg-[#FEE2E2]"><Trash2 size={16} style={{ color: '#EF4444' }} /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Schedule Modal */}
          {showScheduleModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="schedule-modal">
              <div className="bg-white p-8 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}>Schedule Post</h3>
                  <button onClick={() => setShowScheduleModal(null)} className="hover:text-[#FF5722]"><X size={20} /></button>
                </div>
                <p className="text-sm mb-4" style={{ color: '#475569' }}>Pick a date/time or use a quick slot (Mon/Wed/Sat 8 AM IST):</p>
                <div className="space-y-2 mb-4">
                  {getNextScheduleTimes().map((slot, i) => (
                    <button key={i} onClick={() => setScheduleDate(slot.toISOString().slice(0, 16))} data-testid={`schedule-slot-${i}`}
                      className="w-full text-left px-4 py-3 text-sm border transition-colors"
                      style={{ borderColor: scheduleDate === slot.toISOString().slice(0, 16) ? '#FF5722' : '#E2E8F0', backgroundColor: scheduleDate === slot.toISOString().slice(0, 16) ? '#FFF4E6' : 'white', color: '#0A0F1A' }}>
                      <span className="font-medium">{slot.toLocaleDateString('en-IN', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                      <span style={{ color: '#94A3B8' }}> — 8:00 AM IST</span>
                    </button>
                  ))}
                </div>
                <div className="mb-6">
                  <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Or pick custom date/time:</label>
                  <input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} data-testid="schedule-datetime-input"
                    className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] text-sm" style={{ color: '#0A0F1A' }} />
                </div>
                <button onClick={() => handleSchedule(showScheduleModal)} data-testid="schedule-confirm-button"
                  className="w-full py-3 font-semibold inline-flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#FF5722', color: 'white', border: 'none', cursor: 'pointer' }}>
                  <Clock size={16} /> Schedule Post
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogAdmin;
